import { api } from "../base";
import type {
  IIrmaAccountDetail,
  IIrmaAccountEnablementRequest,
  IIrmaAccountEnablementResponse,
  IIrmaAccountFacetsResponse,
  IIrmaAccountListQuery,
  IIrmaAccountListResponse,
  IIrmaAccountSearchRequest,
  IIrmaAccountSearchResponse,
  IrmaAccountColumn,
} from "@pulse-ui/codegen-v2/generated_ts/irma_account";
import type { IIrmaShadowStatus } from "@pulse-ui/codegen-v2/generated_ts/irma_shadow";

export type {
  IIrmaAccountDetail,
  IIrmaAccountEnablement,
  IIrmaAccountEnablementChange,
  IIrmaAccountEnablementRequest,
  IIrmaAccountEnablementResponse,
  IIrmaAccountEntity,
  IIrmaAccountFacetsResponse,
  IIrmaAccountHolder,
  IIrmaAccountLink,
  IIrmaAccountListQuery,
  IIrmaAccountListResponse,
  IIrmaAccountRoleOverride,
  IIrmaAccountSearchResponse,
  IIrmaAccountStatusChange,
  IIrmaAccountSummary,
  IrmaEnablementOutcome,
} from "@pulse-ui/codegen-v2/generated_ts/irma_account";
export type {
  IIrmaShadowDisplayNameMismatch,
  IIrmaShadowEntitySample,
  IIrmaShadowPartitionReplay,
  IIrmaShadowReasonSample,
  IIrmaShadowReplay,
  IIrmaShadowReport,
  IIrmaShadowRoleMismatch,
  IIrmaShadowRoleOverride,
  IIrmaShadowStatus,
} from "@pulse-ui/codegen-v2/generated_ts/irma_shadow";

/** The list query with every filter optional and absent rather than null. */
export type IrmaAccountListParams = {
  [K in keyof IIrmaAccountListQuery]?: NonNullable<IIrmaAccountListQuery[K]>;
};

/** A generated message as sent: nullable fields optional and enums as their string values. */
export type WireParams<T> = T extends string
  ? `${T}`
  : T extends readonly (infer U)[]
    ? WireParams<U>[]
    : T extends object
      ? { [K in keyof T as null extends T[K] ? never : K]: WireParams<T[K]> } & {
          [K in keyof T as null extends T[K] ? K : never]?: WireParams<NonNullable<T[K]>>;
        }
      : T;

export type IrmaAccountSearchParams = WireParams<IIrmaAccountSearchRequest>;
export type IrmaAccountColumnName = `${IrmaAccountColumn}`;

export interface SetIrmaAccountEnablementArgs extends IIrmaAccountEnablementRequest {
  irma_account_id: number;
}

const irmaEndpoints = api.injectEndpoints({
  endpoints: (build) => ({
    /** One page of IRMA accounts from clearstreet-account-manager. */
    getIrmaAccounts: build.query<IIrmaAccountListResponse, IrmaAccountListParams>({
      query: (params) => ({ url: "/api/v1/irma/accounts", params }),
      providesTags: ["IrmaAccounts"],
    }),
    /** A window of IRMA accounts for an AG Grid server-side row model. */
    searchIrmaAccounts: build.mutation<IIrmaAccountSearchResponse, IrmaAccountSearchParams>({
      query: (body) => ({ url: "/api/v1/irma/accounts/search", method: "POST", body }),
    }),
    /** The distinct values of one set-filter column. */
    getIrmaAccountFacets: build.query<IIrmaAccountFacetsResponse, IrmaAccountColumnName>({
      query: (column) => ({ url: "/api/v1/irma/accounts/facets", params: { column } }),
      providesTags: ["IrmaAccounts"],
    }),
    /** One IRMA account with its entity links and histories. */
    getIrmaAccount: build.query<IIrmaAccountDetail, number>({
      query: (id) => ({ url: `/api/v1/irma/accounts/${id}` }),
      providesTags: (_result, _error, id) => [{ type: "IrmaAccount", id }],
    }),
    /** Records a manual enablement decision; admin only. */
    setIrmaAccountEnablement: build.mutation<
      IIrmaAccountEnablementResponse,
      SetIrmaAccountEnablementArgs
    >({
      query: ({ irma_account_id, ...body }) => ({
        url: `/api/v1/irma/accounts/${irma_account_id}/enablement`,
        method: "PUT",
        body,
      }),
      invalidatesTags: (_result, _error, { irma_account_id }) => [
        "IrmaAccounts",
        { type: "IrmaAccount", id: irma_account_id },
        "IrmaShadow",
      ],
    }),
    /** The IRMA boot replay and its latest diff against Talos; admin only. */
    getIrmaShadow: build.query<IIrmaShadowStatus, void>({
      query: () => ({ url: "/api/v1/irma-shadow" }),
      providesTags: ["IrmaShadow"],
    }),
  }),
});

export const {
  useGetIrmaAccountsQuery,
  useSearchIrmaAccountsMutation,
  useLazyGetIrmaAccountFacetsQuery,
  useGetIrmaAccountQuery,
  useSetIrmaAccountEnablementMutation,
  useGetIrmaShadowQuery,
} = irmaEndpoints;
